import { useState } from 'react';
import { User, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const GalleryAccessGate = ({ galleryName, onAccessGranted, open }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const backgroundImageUrl = process.env.REACT_APP_BACKGROUND_IMAGE_URL || 'https://customer-assets.emergentagent.com/job_facedetect-7/artifacts/64z9ml3t_slightly-light-black-concrete-cement-texture-background-dark-grunge-distressed-with-scratches-scary-dark-walls-overlay.jpg';

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);

    // Store user info and grant access
    const userInfo = {
      name: name.trim(),
      email: email.trim(),
      accessedAt: new Date().toISOString()
    };

    // Store in localStorage for session persistence
    localStorage.setItem('gallery_user_info', JSON.stringify(userInfo));

    // Grant access
    setTimeout(() => {
      onAccessGranted(userInfo);
      setSubmitting(false);
    }, 300);
  };

  return (
    <>
      {/* Full screen background */}
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Centered content container */}
        <div className="relative z-10 w-full max-w-lg font-tiq-body">
          <div className="backdrop-blur-sm shadow-2xl w-full" style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 14 }}>
          <div className="p-6 pb-0">
            {/* Turbo 360 Logo (colour, on light card) */}
            <div className="flex justify-center mb-6">
              <img
                src="/turbo-logo-color.png"
                alt="Turbo 360"
                className="h-12 w-auto"
              />
            </div>

            <p className="tiq-kicker text-center" style={{ fontSize: 11, color: '#8A8884' }}>Welcome to</p>
            <h2 className="font-tiq-display text-center uppercase mt-1" style={{ color: '#BF212F', fontWeight: 800, fontSize: 26, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
              {galleryName}
            </h2>
            <p className="text-center text-sm mt-3" style={{ color: '#55555C' }}>
              Please enter your details to access this gallery
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 p-6 pt-4">
            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="access-name" className="text-sm font-medium flex items-center gap-2 text-slate-800">
                <User className="w-4 h-4" style={{color: '#F26B38'}} />
                Full Name
              </Label>
              <Input
                id="access-name"
                type="text"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border-2 border-slate-200 focus:border-red-400"
                autoFocus
              />
            </div>

            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="access-email" className="text-sm font-medium flex items-center gap-2 text-slate-800">
                <Mail className="w-4 h-4" style={{color: '#F26B38'}} />
                Email Address
              </Label>
              <Input
                id="access-email"
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border-2 border-slate-200 focus:border-red-400"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            )}

            {/* Privacy Note */}
            <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border-2 border-slate-200">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-700 leading-relaxed">
                  Your information is used solely for gallery access and activity logging. 
                  We respect your privacy and will never share your details.
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full text-white py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] font-tiq-heading"
              style={{backgroundColor: '#BF212F', borderRadius: 10}}
            >
              {submitting ? 'Entering gallery…' : 'Enter gallery'}
            </Button>
          </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default GalleryAccessGate;
